/* Vathos — behaviour layer. Progressive: every feature degrades to plain HTML. */

/* ---------- Scroll reveal ------------------------------------------------ */
(() => {
  const items = document.querySelectorAll('.reveal');
  if (!items.length || document.documentElement.classList.contains('motion-static') || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
  );
  document.documentElement.classList.add('motion-active');
  items.forEach((el) => io.observe(el));
})();

/* ---------- Cart store --------------------------------------------------- */
const CartStore = {
  routes: window.Shopify?.routes?.root || '/',

  async request(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Cart request failed');
    return data;
  },

  add(items) {
    return this.request(`${this.routes}cart/add.js`, { items });
  },

  change(payload) {
    return this.request(`${this.routes}cart/change.js`, payload);
  },

  async get() {
    const res = await fetch(`${this.routes}cart.js`);
    if (!res.ok) throw new Error('Your cart could not be loaded. Please open the cart page.');
    return res.json();
  },

  async publish(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.toggleAttribute('hidden', cart.item_count === 0);
    });
    await document.querySelector('cart-drawer')?.render(cart);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  },
};

/* ---------- Quantity input ---------------------------------------------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-step]');
      if (!button) return;
      event.preventDefault();
      const step = Number(button.dataset.step);
      const min = Number(this.input.min || 0);
      const next = Math.max(min, Number(this.input.value) + step);
      if (next === Number(this.input.value)) return;
      this.input.value = String(next);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Add to cart -------------------------------------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.errorTarget = this.querySelector('[data-form-error]');
    this.form?.querySelector('[data-variant-id]')?.removeAttribute('disabled');
    this.form?.querySelector('[data-native-variant]')?.setAttribute('disabled', '');
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(event) {
    if (this.dataset.cart === 'page' || !document.querySelector('cart-drawer')) return; // let the browser post normally
    event.preventDefault();
    if (this.loading || this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;
    if (!this.form.reportValidity()) return;

    this.setLoading(true);
    this.showError('');

    const formData = new FormData(this.form);
    const item = {
      id: Number(formData.get('id')),
      quantity: Number(formData.get('quantity') || 1),
    };

    try {
      await CartStore.add([item]);
      const cart = await CartStore.get();
      await CartStore.publish(cart);
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      this.showError(error.message);
      this.querySelector('[data-cart-recovery]')?.removeAttribute('hidden');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(state) {
    this.loading = state;
    this.form?.setAttribute('aria-busy', String(state));
    this.button?.classList.toggle('is-loading', state);
    this.button?.setAttribute('aria-disabled', String(state || this.button.disabled));
  }

  showError(message) {
    if (!this.errorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = !message;
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Variant picker ---------------------------------------------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('[data-product-root]');
    this.variants = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '[]');
    this.addEventListener('change', this.onChange.bind(this));
    const selected = this.selectedOptions;
    this.updateButton(this.variants.find((variant) => variant.options.every((option, index) => option === selected[index])));
  }

  get selectedOptions() {
    return Array.from(this.querySelectorAll('select, input[type="radio"]:checked')).map((el) => el.value);
  }

  onChange() {
    const selected = this.selectedOptions;
    const match = this.variants.find((variant) =>
      variant.options.every((option, index) => option === selected[index])
    );

    this.updateInput(match);
    this.updateButton(match);
    this.updatePrice(match);
    this.updateUrl(match);
    if (match?.media_id) {
      this.root?.querySelector(`[data-media-id="${match.media_id}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }

  updateInput(variant) {
    const input = this.root?.querySelector('product-form input[name="id"]');
    if (input && variant) input.value = String(variant.id);
  }

  updateButton(variant) {
    const button = this.root?.querySelector('product-form [type="submit"]');
    if (!button) return;
    const label = button.querySelector('[data-add-label]') || button;
    const available = variant && variant.available;
    button.setAttribute('aria-disabled', String(!available));
    button.toggleAttribute('disabled', !available);
    label.textContent = variant
      ? available
        ? button.dataset.labelAvailable
        : button.dataset.labelSoldOut
      : button.dataset.labelUnavailable;
  }

  updatePrice(variant) {
    const target = this.root?.querySelector('[data-variant-price]');
    if (!target || !variant) return;
    target.innerHTML = variant.price_html || target.innerHTML;
  }

  updateUrl(variant) {
    if (!variant || this.dataset.updateUrl === 'false') return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url);
  }
}
customElements.define('variant-picker', VariantPicker);

/* ---------- Cart drawer --------------------------------------------------
   A real modal: while open, everything else is inert; on close, focus goes
   back where it came from. All clicks are delegated so re-rendering the
   drawer's content never orphans a listener.
   ------------------------------------------------------------------------ */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-drawer-panel]');
    this.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('cart:open', () => this.open());
    this.errorTarget = this.querySelector('[data-cart-error]');
    this.renderVersion = 0;
    document.addEventListener('keydown', (event) => {
      if (!this.hasAttribute('open')) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'Tab') {
        const controls = [...this.querySelectorAll('a[href], button:not([disabled]), input, textarea, summary, [tabindex="0"]')].filter((el) => el.getClientRects().length);
        const first = controls[0];
        const last = controls.at(-1);
        if (!first) { event.preventDefault(); this.dialog?.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    });
  }

  siblings() {
    const result = [];
    let branch = this;
    while (branch.parentElement) {
      result.push(...[...branch.parentElement.children].filter((el) => el !== branch && el.tagName !== 'SCRIPT'));
      if (branch.parentElement === document.body) break;
      branch = branch.parentElement;
    }
    return result;
  }

  open() {
    if (this.hasAttribute('open')) return;
    this.returnFocus = document.activeElement;
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    this.inerted = this.siblings().filter((el) => !el.hasAttribute('inert'));
    this.inerted.forEach((el) => el.setAttribute('inert', ''));
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.dialog?.focus();
  }

  close() {
    this.inerted?.forEach((el) => el.removeAttribute('inert'));
    if (this.returnFocus?.isConnected) this.returnFocus.focus();
    else document.querySelector('.header__cart')?.focus();
    this.returnFocus = null;
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = this.previousOverflow || '';
  }

  async onClick(event) {
    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    const button = event.target.closest('[data-line-change]');
    if (!button) return;
    event.preventDefault();
    if (this.hasAttribute('aria-busy')) return;
    if (this.errorTarget) this.errorTarget.hidden = true;
    // Remember which control was used so focus can land on its successor
    // after the innerHTML swap destroys this one.
    this.pendingFocus = { line: button.dataset.line, label: button.getAttribute('aria-label') };
    this.setAttribute('aria-busy', 'true');
    try {
      const cart = await CartStore.change({
        line: Number(button.dataset.line),
        quantity: Number(button.dataset.quantity),
      });
      await CartStore.publish(cart);
    } catch (error) {
      this.pendingFocus = null;
      if (this.errorTarget) this.errorTarget.hidden = false;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  async render(cart) {
    const version = ++this.renderVersion;
    const res = await fetch(`${CartStore.routes}?section_id=cart-drawer`);
    if (!res.ok) throw new Error('Your cart could not be refreshed. Please open the cart page.');
    const html = new DOMParser().parseFromString(await res.text(), 'text/html');
    if (version !== this.renderVersion) return;
    const fresh = html.querySelector('[data-drawer-content]');
    const current = this.querySelector('[data-drawer-content]');
    // The whole content — items AND the checkout footer. Swapping only the
    // body once shipped a drawer whose first added item had no checkout
    // button, because the server only renders the footer for non-empty carts.
    if (!fresh || !current) throw new Error('Your cart could not be refreshed. Please open the cart page.');
    current.innerHTML = fresh.innerHTML;

    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.toggleAttribute('hidden', cart.item_count === 0);
    });

    if (this.pendingFocus && this.hasAttribute('open')) {
      const { line, label } = this.pendingFocus;
      this.pendingFocus = null;
      const successor =
        (label && this.querySelector(`[data-line-change][data-line="${line}"][aria-label="${label}"]`)) ||
        this.querySelector('[data-line-change]') ||
        this.dialog;
      successor?.focus();
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- Floating header ---------------------------------------------
   On templates where the header sits over a full-bleed hero it starts
   transparent and takes a ground once the hero has passed. Uses a sentinel
   rather than a scroll handler so nothing runs on the main thread per frame.
   ------------------------------------------------------------------------ */
(() => {
  const header = document.querySelector('.template-index .shopify-section--header .header');
  if (!header) return;

  // Floating transparent over the page only makes sense when the page opens
  // with a full-bleed image. If the merchant reorders the homepage, the
  // header takes its ground immediately instead of hovering invisibly.
  const first = document.querySelector('.main > .shopify-section');
  const lead = first?.querySelector('.hero, .bleed, .gate');
  if (!lead || !('IntersectionObserver' in window)) {
    header.classList.add('header--solid');
    return;
  }

  // Watch the lead section itself — no guessed heights, so the compact
  // mobile hero and the full-screen desktop hero both hand over correctly.
  new IntersectionObserver(
    ([entry]) => header.classList.toggle('header--solid', !entry.isIntersecting),
    { rootMargin: '-72px 0px 0px 0px', threshold: 0 }
  ).observe(lead);
})();

/* ---------- Sticky header ------------------------------------------------
   position: sticky on the header element itself never worked: Shopify wraps
   each section in a div exactly the header's height, and a sticky element
   cannot travel outside its containing block. The wrapper is what sticks.
   ------------------------------------------------------------------------ */
(() => {
  if (document.body.classList.contains('template-index')) return; // fixed float handles index
  document.querySelector('.header--sticky')
    ?.closest('.shopify-section')
    ?.classList.add('shopify-section--stuck');
})();
